// Global error handler for Chrome extension conflicts
class ErrorHandler {
  constructor() {
    this.setupGlobalErrorHandling();
  }

  setupGlobalErrorHandling() {
    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.warn('Unhandled promise rejection:', event.reason);
      
      // Check if it's a Chrome extension error
      if (this.isChromeExtensionError(event.reason)) {
        console.warn('Chrome extension error detected and ignored:', event.reason);
        event.preventDefault(); // Prevent the error from bubbling up
        return false;
      }
    });

    // Catch global errors
    window.addEventListener('error', (event) => {
      console.warn('Global error:', event.error);
      
      // Check if it's a Chrome extension error
      if (this.isChromeExtensionError(event.error) || 
          this.isChromeExtensionError(event.message) || 
          (event.filename && event.filename.includes('chrome-extension://'))) {
        console.warn('Chrome extension error detected and ignored:', {
          message: event.message,
          filename: event.filename,
          error: event.error
        });
        event.preventDefault(); // Prevent the error from bubbling up
      }
    });

    // Override console.error to filter out Chrome extension errors
    const originalConsoleError = console.error;
    console.error = (...args) => {
      const errorMessage = args.join(' ');
      
      // Don't log Chrome extension errors
      if (this.isChromeExtensionError(errorMessage)) {
        console.warn('Chrome extension error filtered:', errorMessage);
        return;
      }
      
      originalConsoleError.apply(console, args);
    };

    // Suppress React's development error overlay for MetaMask errors only
    if (process.env.NODE_ENV === 'development') {
      this.suppressReactErrorOverlay();
    }
  }

  suppressReactErrorOverlay() {
    // Override reportError function specifically for React's error overlay
    const originalReportError = window.reportError || window.__reportError;
    
    if (originalReportError) {
      window.reportError = window.__reportError = (error) => {
        if (this.isChromeExtensionError(error)) {
          console.warn('React Error Overlay: MetaMask error suppressed:', error);
          return;
        }
        return originalReportError(error);
      };
    }

    // Also intercept the React error overlay's error handling
    const checkForOverlay = () => {
      const overlay = document.querySelector('iframe[data-test-id="error-overlay"]') ||
                     document.querySelector('[data-react-error-overlay]') ||
                     document.querySelector('div[style*="position: fixed"][style*="z-index"]');
      
      if (overlay && overlay.contentDocument) {
        const errorText = overlay.contentDocument.body?.textContent || overlay.textContent || '';
        if (errorText.includes('Failed to connect to MetaMask') || 
            errorText.includes('chrome-extension://') ||
            errorText.includes('inpage.js')) {
          console.warn('React Error Overlay: Removing MetaMask error overlay');
          overlay.style.display = 'none';
          setTimeout(() => overlay.remove(), 100);
        }
      }
    };

    // Check for overlay on DOM mutations
    const observer = new MutationObserver(() => {
      checkForOverlay();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also check periodically in case the overlay appears
    setInterval(checkForOverlay, 1000);
  }

  isChromeExtensionError(error) {
    if (!error) return false;
    
    const errorString = error.toString();
    const errorStack = error.stack || '';
    
    // Check for common Chrome extension error patterns
    const chromeExtensionPatterns = [
      'chrome-extension://',
      'Cannot read properties of null (reading \'type\')',
      'Cannot read property \'type\' of null',
      'inpage.js',
      'content_script',
      'extension context invalidated',
      'Extension context invalidated',
      'chrome.runtime.onMessage',
      'chrome.tabs',
      'Failed to connect to MetaMask',
      'MetaMask',
      'metamask'
    ];
    
    return chromeExtensionPatterns.some(pattern => 
      errorString.includes(pattern) || errorStack.includes(pattern)
    );
  }

  // Safe object property access
  static safeGet(obj, path, defaultValue = null) {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue;
      }, obj);
    } catch (error) {
      console.warn('SafeGet error:', error);
      return defaultValue;
    }
  }

  // Safe type checking
  static safeType(obj) {
    try {
      return obj && typeof obj === 'object' && obj.type ? obj.type : null;
    } catch (error) {
      console.warn('SafeType error:', error);
      return null;
    }
  }

  // Safe event handling
  static safeEventHandler(handler) {
    return function(event) {
      try {
        if (event && typeof event === 'object') {
          return handler(event);
        }
      } catch (error) {
        console.warn('Event handler error:', error);
      }
    };
  }
}

// Initialize global error handler
const errorHandler = new ErrorHandler();

export default ErrorHandler; 